import React, { useEffect, useMemo, useState } from "react";
import {
  Search, Filter, Download, MoreHorizontal, CheckCircle2, Clock, XCircle,
  UserPlus, Mail, Phone, Shield, Edit2, Ban, KeyRound, Trash2, X, ShieldCheck,
  Users as UsersIcon, UserCheck, ShieldAlert, Car, LayoutGrid, List,
  Upload, FileSpreadsheet, AlertTriangle,
} from "lucide-react";
import { ROLE_META, roleLabel, roleColor } from "../config/brand";
import { dataApi, formatApiError } from "../api/client";
import { EmptyState } from "./Dashboard";
import { Users } from "lucide-react";

const ROLE_TABS = ["Todos", "super_admin", "admin_ops", "dispatcher", "accountant", "driver", "socio", "passenger"];

export default function UsersManagement() {
  const [users, setUsers] = useState(null);
  const [tab, setTab] = useState("Todos");
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("users_view") || "kanban");
  useEffect(() => { try { localStorage.setItem("users_view", viewMode); } catch { /* ignore */ } }, [viewMode]);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [err, setErr] = useState("");

  const load = () => dataApi.users().then((data) => {
    setUsers(data);
    // Sync selected user with fresh data if still exists
    setSelected((prev) => prev ? data.find((u) => u.id === prev.id) || null : null);
  }).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const handleVerify = async (user) => {
    if (!window.confirm(`¿Marcar a "${user.name}" como VERIFICADO? Podrá usar todas las funciones del sistema.`)) return;
    try {
      await dataApi.verifyUser(user.id);
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const filtered = useMemo(() => {
    if (!users) return [];
    return users.filter((u) => {
      const roleOk = tab === "Todos" || u.role === tab;
      const q = search.toLowerCase();
      const searchOk = !q || u.name.toLowerCase().includes(q)
                        || (u.phone || "").includes(search)
                        || (u.email || "").toLowerCase().includes(q);
      return roleOk && searchOk;
    });
  }, [users, tab, search]);

  const stats = useMemo(() => {
    if (!users) return { total: 0, active: 0, pending: 0, drivers: 0 };
    return {
      total: users.length,
      active: users.filter((u) => u.is_active).length,
      pending: users.filter((u) => !u.verified).length,
      drivers: users.filter((u) => u.role === "driver").length,
    };
  }, [users]);

  return (
    <div className="space-y-4 fade-in" data-testid="users-view">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-mdd-muted flex items-center gap-2">
            <UsersIcon className="w-3 h-3" /> ADMINISTRACIÓN
          </div>
          <h1 className="font-display font-semibold text-2xl md:text-3xl text-mdd-text tracking-tight mt-1">Gestión de usuarios</h1>
          <p className="text-sm text-mdd-muted mt-1">
            Administra socios, conductores, pasajeros, admins y despachadores. Verifica cuentas nuevas antes de darles acceso completo.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="mdd-btn mdd-btn-ghost" data-testid="users-btn-import-csv" onClick={() => setShowImport(true)}>
            <Upload className="w-4 h-4" strokeWidth={1.75}/>Importar CSV
          </button>
          <button className="mdd-btn mdd-btn-ghost"><Download className="w-4 h-4" strokeWidth={1.75}/>Exportar</button>
          <button className="mdd-btn mdd-btn-primary" data-testid="users-btn-new" onClick={() => setShowCreate(true)}>
            <UserPlus className="w-4 h-4" strokeWidth={2}/>Nuevo usuario
          </button>
        </div>
      </div>

      {err && <div className="mdd-card p-3 border-mdd-danger/30 text-mdd-danger text-sm">{err}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MiniStat label="Total usuarios" value={stats.total} icon={UsersIcon} accent="indigo" />
        <MiniStat label="Activos" value={stats.active} icon={UserCheck} accent="success" />
        <MiniStat label="Sin verificar" value={stats.pending} icon={ShieldAlert} accent="warning" />
        <MiniStat label="Conductores" value={stats.drivers} icon={Car} accent="neon" />
      </div>

      <div className="office-card">
        {/* Tabs */}
        <div className="border-b border-neutral-200 flex items-center overflow-x-auto px-2">
          {ROLE_TABS.map((r) => {
            const count = r === "Todos" ? (users?.length || 0) : (users || []).filter((u) => u.role === r).length;
            const label = r === "Todos" ? "Todos" : roleLabel(r);
            return (
              <button key={r} data-testid={`users-tab-${r}`} onClick={() => setTab(r)}
                      className={`ribbon-tab ${tab === r ? "active" : ""} flex items-center gap-1.5 whitespace-nowrap`}>
                {label}
                <span className={`text-[10px] font-mono-grotesk px-1.5 py-0.5 rounded ${tab === r ? "bg-[#EEF0FC] text-[#3949AB]" : "bg-neutral-100 text-neutral-500"}`}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* Toolbar */}
        <div className="p-3 flex items-center gap-2 border-b border-neutral-200 bg-[#F8F9FC]">
          <div className="flex items-center gap-2 flex-1 max-w-md bg-white border border-neutral-300 rounded px-2.5 py-1.5 focus-within:border-[#3949AB]">
            <Search className="w-3.5 h-3.5 text-neutral-500" strokeWidth={2.5}/>
            <input data-testid="users-search" value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Buscar por nombre, teléfono o correo..."
                   className="bg-transparent outline-none flex-1 text-sm" />
            {search && <button onClick={() => setSearch("")}><X className="w-3.5 h-3.5 text-neutral-400" strokeWidth={2.5}/></button>}
          </div>
          <div className="flex items-center gap-0.5 mdd-card !p-1">
            <button
              data-testid="users-view-kanban"
              onClick={() => setViewMode("kanban")}
              title="Vista Kanban"
              className={`px-2.5 py-1.5 rounded-md text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 transition-all ${viewMode === "kanban" ? "bg-mdd-neon/20 text-mdd-lime border border-mdd-neon/30" : "text-mdd-muted hover:text-mdd-text hover:bg-white/5"}`}
            >
              <LayoutGrid className="w-3.5 h-3.5" strokeWidth={2}/> Kanban
            </button>
            <button
              data-testid="users-view-table"
              onClick={() => setViewMode("table")}
              title="Vista Tabla"
              className={`px-2.5 py-1.5 rounded-md text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 transition-all ${viewMode === "table" ? "bg-mdd-neon/20 text-mdd-lime border border-mdd-neon/30" : "text-mdd-muted hover:text-mdd-text hover:bg-white/5"}`}
            >
              <List className="w-3.5 h-3.5" strokeWidth={2}/> Tabla
            </button>
          </div>
          <div className="text-xs text-neutral-500 ml-2 font-mono">
            {filtered.length} de {users?.length || 0}
          </div>
        </div>

        {users === null ? (
          <div className="p-8 text-center text-sm text-neutral-500">Cargando usuarios...</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={UsersIcon}
            title={search ? "Sin resultados" : "No hay usuarios en esta categoría"}
            desc={search ? "Intenta con otro término" : 'Crea el primer usuario con el botón "Nuevo usuario"'}
          />
        ) : viewMode === "kanban" ? (
          <KanbanBoard users={filtered} onOpen={setSelected} onVerify={handleVerify} activeRole={tab} />
        ) : (
          <div className="overflow-x-auto">
            <table className="office-table">
              <thead>
                <tr>
                  <th style={{ width: "30px" }}></th>
                  <th>Usuario</th><th>Contacto</th><th>Rol</th><th>Estado</th><th>Verificado</th><th>Último acceso</th>
                  <th style={{ width: "40px" }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <UserRow key={u.id} user={u} onOpen={() => setSelected(u)} onVerify={handleVerify} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && <UserDetailPanel user={selected} onClose={() => setSelected(null)} onChanged={() => { load(); }} />}
      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {showImport && <ImportPassengersModal onClose={() => setShowImport(false)} onImported={() => { load(); }} />}
    </div>
  );
}

const KANBAN_COLUMNS = [
  { role: "super_admin",  label: "Super Admin",  color: "danger"  },
  { role: "admin_ops",    label: "Admin Ops",    color: "danger"  },
  { role: "dispatcher",   label: "Despachador",  color: "warning" },
  { role: "contador",     label: "Contador",     color: "warning" },
  { role: "driver",       label: "Conductor",    color: "indigo"  },
  { role: "socio",        label: "Socio",        color: "indigo"  },
  { role: "passenger",    label: "Pasajero",     color: "neon"    },
];

const KanbanCard = ({ user, onOpen, onVerify }) => {
  const initials = user.name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase();
  const last = user.last_login
    ? new Date(user.last_login).toLocaleString("es-PE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "Sin ingresos";
  return (
    <button
      data-testid={`kanban-card-${user.id}`}
      onClick={onOpen}
      className={`mdd-card p-3 text-left w-full block group hover:border-mdd-neon/50 transition-all cursor-pointer ${!user.is_active ? "opacity-60" : ""}`}
    >
      <div className="flex items-start gap-2.5">
        <div className="w-9 h-9 shrink-0 rounded-full bg-gradient-to-br from-mdd-neon to-mdd-indigo text-white flex items-center justify-center font-semibold text-xs ring-1 ring-white/10">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-mdd-text text-sm truncate">{user.name}</div>
          <div className="text-[11px] text-mdd-muted font-mono truncate">
            {user.phone || user.email || "—"}
          </div>
        </div>
        {user.verified ? (
          <ShieldCheck className="w-4 h-4 text-mdd-success shrink-0" strokeWidth={2.5} />
        ) : (
          <ShieldAlert className="w-4 h-4 text-mdd-yellow shrink-0 animate-pulse" strokeWidth={2.5} />
        )}
      </div>
      <div className="mt-3 flex items-center gap-1.5 flex-wrap">
        {user.is_active ? (
          <span className="mdd-chip mdd-chip-success !text-[9px] !py-0.5 !px-1.5">
            <CheckCircle2 className="w-2.5 h-2.5" strokeWidth={2.5} />Activo
          </span>
        ) : (
          <span className="mdd-chip mdd-chip-danger !text-[9px] !py-0.5 !px-1.5">
            <XCircle className="w-2.5 h-2.5" strokeWidth={2.5} />Inactivo
          </span>
        )}
        {user.verified ? (
          <span className="mdd-chip mdd-chip-success !text-[9px] !py-0.5 !px-1.5">Verificado</span>
        ) : (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onVerify && onVerify(user); }}
            className="mdd-btn mdd-btn-neon !py-0.5 !px-2 !text-[10px] cursor-pointer"
            data-testid={`kanban-verify-${user.id}`}
          >
            <ShieldCheck className="w-2.5 h-2.5" strokeWidth={2.5} />Verificar
          </span>
        )}
      </div>
      <div className="mt-2 text-[10px] text-mdd-muted font-mono flex items-center gap-1">
        <Clock className="w-2.5 h-2.5" />{last}
      </div>
    </button>
  );
};

const KanbanBoard = ({ users, onOpen, onVerify, activeRole }) => {
  // Si hay un rol activo (no "Todos"), mostramos solo esa columna
  const cols = activeRole !== "Todos"
    ? KANBAN_COLUMNS.filter((c) => c.role === activeRole)
    : KANBAN_COLUMNS;
  return (
    <div className="p-4 overflow-x-auto">
      <div
        data-testid="users-kanban-board"
        className="grid gap-4"
        style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(240px, 1fr))` }}
      >
        {cols.map((col) => {
          const items = users.filter((u) => u.role === col.role);
          const pendingCount = items.filter((u) => !u.verified).length;
          return (
            <div
              key={col.role}
              data-testid={`kanban-col-${col.role}`}
              className="mdd-card !p-0 overflow-hidden flex flex-col"
              style={{ minHeight: "300px" }}
            >
              <div className={`px-3 py-2.5 border-b border-white/5 flex items-center justify-between sticky top-0 backdrop-blur-md`}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${
                    col.color === "danger"  ? "bg-mdd-danger" :
                    col.color === "warning" ? "bg-mdd-yellow" :
                    col.color === "indigo"  ? "bg-mdd-neon"   :
                                              "bg-mdd-lime"
                  }`} />
                  <div className="text-[11px] font-mono uppercase tracking-widest text-mdd-text font-semibold">{col.label}</div>
                </div>
                <div className="flex items-center gap-1">
                  <span className="mdd-chip mdd-chip-indigo !text-[10px] !py-0 !px-1.5">{items.length}</span>
                  {pendingCount > 0 && (
                    <span className="mdd-chip mdd-chip-danger !text-[10px] !py-0 !px-1.5" title={`${pendingCount} sin verificar`}>
                      {pendingCount}!
                    </span>
                  )}
                </div>
              </div>
              <div className="p-2 space-y-2 flex-1">
                {items.length === 0 ? (
                  <div className="text-center text-[11px] text-mdd-muted py-8 italic">Sin usuarios</div>
                ) : (
                  items.map((u) => (
                    <KanbanCard key={u.id} user={u} onOpen={() => onOpen(u)} onVerify={onVerify} />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


const MiniStat = ({ label, value, accent = "neutral", icon: Icon }) => {
  const toneMap = {
    neutral: { border: "border-mdd-neon/30", bg: "bg-mdd-neon/10",    text: "text-mdd-neon" },
    indigo:  { border: "border-mdd-neon/30", bg: "bg-mdd-neon/10",    text: "text-mdd-neon" },
    success: { border: "border-mdd-success/30", bg: "bg-mdd-success/10", text: "text-mdd-success" },
    warning: { border: "border-mdd-yellow/30", bg: "bg-mdd-yellow/10", text: "text-mdd-yellow" },
    neon:    { border: "border-mdd-lime/30", bg: "bg-mdd-lime/10",    text: "text-mdd-lime" },
  };
  const t = toneMap[accent] || toneMap.neutral;
  return (
    <div className="mdd-card p-5 animate-fade-up">
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-mdd-muted">{label}</div>
        {Icon && (
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${t.bg} ${t.border} ${t.text}`}>
            <Icon className="w-4 h-4" strokeWidth={1.75}/>
          </div>
        )}
      </div>
      <div className={`mdd-kpi-number ${accent === "neon" ? "mdd-kpi-neon" : ""} text-3xl leading-none`}>
        {value}
      </div>
    </div>
  );
};

const UserRow = ({ user, onOpen, onVerify }) => {
  const initials = user.name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase();
  const last = user.last_login ? new Date(user.last_login).toLocaleString("es-PE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
  return (
    <tr onClick={onOpen} className="cursor-pointer" data-testid={`user-row-${user.id}`}>
      <td><input type="checkbox" onClick={(e) => e.stopPropagation()} className="rounded border-neutral-300 text-[#3949AB]"/></td>
      <td>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-mdd-neon to-mdd-indigo text-white flex items-center justify-center font-semibold text-xs ring-1 ring-white/10">{initials}</div>
          <div>
            <div className="font-medium text-neutral-900">{user.name}</div>
            {user.email && <div className="text-[11px] text-neutral-400">{user.email}</div>}
          </div>
        </div>
      </td>
      <td className="text-neutral-600"><div className="font-mono text-xs">{user.phone || "—"}</div></td>
      <td><span className={`chip chip-${roleColor(user.role)}`}>{roleLabel(user.role)}</span></td>
      <td>{user.is_active ? <span className="chip chip-success"><CheckCircle2 className="w-3 h-3" strokeWidth={2.5}/>Activo</span> : <span className="chip chip-danger"><XCircle className="w-3 h-3" strokeWidth={2.5}/>Inactivo</span>}</td>
      <td>
        {user.verified ? (
          <span data-testid={`user-verified-${user.id}`} className="inline-flex items-center gap-1 text-[#107C10] text-xs font-semibold">
            <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.5}/>Verificado
          </span>
        ) : (
          <button
            data-testid={`user-verify-btn-${user.id}`}
            onClick={(e) => { e.stopPropagation(); onVerify && onVerify(user); }}
            className="mdd-btn mdd-btn-neon !py-1 !px-2.5 !text-[11px]"
            title="Marcar como verificado"
          >
            <ShieldCheck className="w-3 h-3" strokeWidth={2.5}/>
            Verificar
          </button>
        )}
      </td>
      <td className="text-xs text-neutral-500 font-mono">{last}</td>
      <td><button onClick={(e) => { e.stopPropagation(); onOpen(); }} className="mdd-btn mdd-btn-ghost !p-1.5"><MoreHorizontal className="w-4 h-4" strokeWidth={2}/></button></td>
    </tr>
  );
};

const UserDetailPanel = ({ user, onClose, onChanged }) => {
  const initials = user.name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase();
  const meta = ROLE_META[user.role] || {};
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(null); // { type, message }
  const [toast, setToast] = useState("");
  const [editing, setEditing] = useState(false);
  const [tempPass, setTempPass] = useState("");

  const doToggle = async () => {
    setBusy("toggle");
    try {
      await dataApi.toggleUser(user.id);
      setToast(user.is_active ? "Usuario desactivado" : "Usuario activado");
      onChanged && onChanged();
    } catch (e) { setToast(formatApiError(e)); }
    finally { setBusy(""); setConfirm(null); }
  };
  const doDelete = async () => {
    setBusy("delete");
    try {
      await dataApi.deleteUser(user.id);
      setToast("Usuario eliminado");
      onChanged && onChanged();
      setTimeout(onClose, 700);
    } catch (e) { setToast(formatApiError(e)); }
    finally { setBusy(""); setConfirm(null); }
  };
  const doReset = async () => {
    setBusy("reset");
    try {
      const res = await dataApi.resetPassword(user.id);
      setTempPass(res.temporary_password);
    } catch (e) { setToast(formatApiError(e)); }
    finally { setBusy(""); setConfirm(null); }
  };
  const doVerify = async () => {
    setBusy("verify");
    try {
      await dataApi.verifyUser(user.id);
      setToast(user.verified ? "Verificación removida" : "Usuario verificado ✔");
      onChanged && onChanged();
    } catch (e) { setToast(formatApiError(e)); }
    finally { setBusy(""); setConfirm(null); }
  };
  const doUnlock = async () => {
    setBusy("unlock");
    try {
      await dataApi.unlockUser(user.id);
      setToast("Usuario desbloqueado ✔");
      onChanged && onChanged();
    } catch (e) { setToast(formatApiError(e)); }
    finally { setBusy(""); setConfirm(null); }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose}></div>
      <aside className="fixed top-12 right-0 bottom-0 w-full max-w-md bg-white border-l border-neutral-200 shadow-xl z-50 overflow-y-auto fade-in" data-testid="user-detail-panel">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-5 py-3 flex items-center justify-between">
          <h3 className="font-display font-semibold text-lg">Detalle de usuario</h3>
          <button onClick={onClose} className="btn-ghost !p-1.5" data-testid="user-detail-close"><X className="w-4 h-4" strokeWidth={2.5}/></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-[#EEF0FC] text-[#3949AB] flex items-center justify-center font-semibold text-xl">{initials}</div>
            <div>
              <h2 className="font-display font-bold text-xl">{user.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className={`chip chip-${roleColor(user.role)}`}>{roleLabel(user.role)}</span>
                {user.is_active ? <span className="chip chip-success">Activo</span> : <span className="chip chip-danger">Inactivo</span>}
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <InfoField icon={Phone} label="Teléfono" value={user.phone || "—"} />
            {user.email && <InfoField icon={Mail} label="Correo" value={user.email} />}
            <InfoField icon={Shield} label="Rol" value={`${roleLabel(user.role)} — ${meta.desc || ""}`} />
            <InfoField icon={CheckCircle2} label="Verificado" value={user.verified ? "Sí" : "No"} />
            <InfoField icon={Clock} label="Creado" value={new Date(user.created_at).toLocaleString("es-PE")} />
          </div>

          {tempPass && (
            <div className="chip chip-warning !flex-col !items-start w-full" data-testid="temp-password-banner">
              <div className="font-semibold text-xs mb-1">Contraseña temporal generada</div>
              <div className="font-mono text-sm bg-white/60 px-2 py-1 rounded select-all mt-1 block">{tempPass}</div>
              <div className="text-[10px] mt-1 opacity-80">Comunica esta contraseña al usuario por un canal seguro.</div>
            </div>
          )}
          {toast && (
            <div className="chip chip-info" data-testid="detail-toast">{toast}</div>
          )}

          <div className="border-t border-neutral-200 pt-4 space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2">Acciones</div>
            {!user.verified && (
              <button
                className="mdd-btn mdd-btn-neon w-full justify-start animate-pulse-neon"
                onClick={() => setConfirm({ type: "verify", message: `¿Marcar a ${user.name} como VERIFICADO? Podrá usar todas las funciones del sistema.` })}
                disabled={busy === "verify"}
                data-testid="user-action-verify"
              >
                <ShieldCheck className="w-4 h-4" strokeWidth={2.5}/>{busy === "verify" ? "Verificando..." : "Verificar usuario"}
              </button>
            )}
            {user.verified && (
              <button
                className="mdd-btn mdd-btn-ghost w-full justify-start"
                onClick={() => setConfirm({ type: "verify", message: `¿Quitar verificación a ${user.name}?` })}
                disabled={busy === "verify"}
                data-testid="user-action-unverify"
              >
                <ShieldCheck className="w-4 h-4 text-mdd-success" strokeWidth={2.5}/>{busy === "verify" ? "Procesando..." : "Quitar verificación"}
              </button>
            )}
            <button
              className="btn-secondary w-full justify-start"
              onClick={() => setEditing(true)}
              data-testid="user-action-edit"
            >
              <Edit2 className="w-3.5 h-3.5" strokeWidth={2.5}/>Editar
            </button>
            <button
              className="btn-secondary w-full justify-start"
              onClick={() => setConfirm({ type: "reset", message: `Se generará una contraseña temporal para ${user.name}. ¿Continuar?` })}
              disabled={busy === "reset"}
              data-testid="user-action-reset"
            >
              <KeyRound className="w-3.5 h-3.5" strokeWidth={2.5}/>{busy === "reset" ? "Procesando..." : "Restablecer contraseña"}
            </button>
            <button
              className="btn-secondary w-full justify-start !text-[#3B82F6] !border-[#93C5FD]"
              onClick={() => setConfirm({ type: "unlock", message: `Desbloquear cuenta de ${user.name} y limpiar intentos fallidos. ¿Continuar?` })}
              disabled={busy === "unlock"}
              data-testid="user-action-unlock"
            >
              <KeyRound className="w-3.5 h-3.5" strokeWidth={2.5}/>{busy === "unlock" ? "Desbloqueando..." : "Desbloquear usuario"}
            </button>
            <button
              className="btn-secondary w-full justify-start"
              onClick={() => setConfirm({ type: "toggle", message: `¿${user.is_active ? "Desactivar" : "Activar"} a ${user.name}?` })}
              disabled={busy === "toggle"}
              data-testid="user-action-toggle"
            >
              <Ban className="w-3.5 h-3.5" strokeWidth={2.5}/>{user.is_active ? "Desactivar" : "Activar"}
            </button>
            <button
              className="btn-secondary w-full justify-start !text-[#D13438] !border-[#F1B0B7]"
              onClick={() => setConfirm({ type: "delete", message: `¿Eliminar permanentemente a ${user.name}? Esta acción no se puede deshacer.` })}
              disabled={busy === "delete"}
              data-testid="user-action-delete"
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={2.5}/>{busy === "delete" ? "Eliminando..." : "Eliminar"}
            </button>
          </div>
        </div>

        {editing && (
          <EditUserModal user={user} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); onChanged && onChanged(); setToast("Usuario actualizado"); }} />
        )}
        {confirm && (
          <ConfirmDialog
            message={confirm.message}
            danger={confirm.type === "delete"}
            onCancel={() => setConfirm(null)}
            onConfirm={
              confirm.type === "toggle" ? doToggle :
              confirm.type === "delete" ? doDelete :
              confirm.type === "verify" ? doVerify :
              confirm.type === "unlock" ? doUnlock :
              doReset
            }
          />
        )}
      </aside>
    </>
  );
};

const ConfirmDialog = ({ message, danger, onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-black/40" data-testid="confirm-dialog">
    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
      <div className="text-sm text-neutral-800 mb-6">{message}</div>
      <div className="flex justify-end gap-2">
        <button className="btn-secondary" onClick={onCancel} data-testid="confirm-cancel">Cancelar</button>
        <button
          className={danger ? "btn-primary !bg-[#D13438] !border-[#D13438] hover:!bg-[#A80000]" : "btn-primary"}
          onClick={onConfirm}
          data-testid="confirm-accept"
        >
          {danger ? "Sí, eliminar" : "Sí, continuar"}
        </button>
      </div>
    </div>
  </div>
);

const EditUserModal = ({ user, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    phone: user.phone || "",
    role: user.role,
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      await dataApi.updateUser(user.id, form);
      onSaved();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[60]" onClick={onClose}></div>
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-6" onClick={onClose}>
        <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-lg shadow-xl max-w-md w-full" data-testid="edit-user-modal">
          <div className="border-b border-neutral-200 px-5 py-3 flex items-center justify-between">
            <h3 className="font-display font-semibold text-lg">Editar usuario</h3>
            <button type="button" onClick={onClose} className="btn-ghost !p-1.5"><X className="w-4 h-4" strokeWidth={2.5}/></button>
          </div>
          <div className="p-5 space-y-3">
            <FieldRow label="Nombre completo *">
              <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="office-input" required data-testid="edit-user-name" />
            </FieldRow>
            <FieldRow label="Teléfono *">
              <input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="office-input" required data-testid="edit-user-phone" />
            </FieldRow>
            <FieldRow label="Correo electrónico">
              <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="office-input" data-testid="edit-user-email" />
            </FieldRow>
            <FieldRow label="Rol *">
              <select value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} className="office-input" data-testid="edit-user-role">
                {["super_admin","admin_ops","dispatcher","accountant","driver","socio","passenger","auditor"].map((r) => (
                  <option key={r} value={r}>{roleLabel(r)}</option>
                ))}
              </select>
            </FieldRow>
            {error && <div className="chip chip-danger" data-testid="edit-user-error">{error}</div>}
          </div>
          <div className="border-t border-neutral-200 px-5 py-3 flex justify-end gap-2 bg-neutral-50">
            <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="edit-user-submit">
              {saving ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const InfoField = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 bg-neutral-50 border border-neutral-200 rounded flex items-center justify-center shrink-0">
      <Icon className="w-3.5 h-3.5 text-neutral-500" strokeWidth={2.5}/>
    </div>
    <div className="flex-1">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="text-sm text-neutral-900 font-medium">{value}</div>
    </div>
  </div>
);

// ═════════════════════════════════════════════════════════════════════
// Modal: Crear nuevo usuario · redesign Sep 2026 (fondo blanco, validación)
// ═════════════════════════════════════════════════════════════════════
const PHONE_REGEX = /^\+?\d{9,15}$/;
const PLATE_REGEX = /^[A-Z0-9]{3}-?[A-Z0-9]{3,4}$/;

const CreateUserModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "passenger",
                                      plate: "", brand: "", model: "", color: "", passenger_capacity: 4, vehicle_type: "sedan" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const isSocio = form.role === "socio";
  const phoneClean = (form.phone || "").replace(/\s|-/g, "");
  const plateClean = (form.plate || "").toUpperCase().replace(/\s/g, "");
  const phoneValid = PHONE_REGEX.test(phoneClean);
  const plateValid = !isSocio || PLATE_REGEX.test(plateClean);
  const nameValid = (form.name || "").trim().length >= 3;
  const vehicleFieldsValid = !isSocio || (form.model && form.plate);
  const canSubmit = !saving && nameValid && phoneValid && plateValid && vehicleFieldsValid;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!canSubmit) return;
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        email: form.email?.trim() || null,
        phone: phoneClean,
        role: form.role,
      };
      if (isSocio) {
        Object.assign(body, {
          plate: plateClean,
          brand: form.brand?.trim() || null,
          model: form.model.trim(),
          color: form.color?.trim() || null,
          passenger_capacity: parseInt(form.passenger_capacity, 10) || 4,
          vehicle_type: form.vehicle_type,
        });
      }
      await dataApi.createUser(body);
      onCreated();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose}></div>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <form
          onSubmit={submit}
          className="rounded-xl shadow-2xl w-full max-w-2xl fade-in max-h-[92vh] overflow-y-auto"
          style={{ backgroundColor: "#C6E90C", color: "#0A1728" }}
          data-testid="create-user-modal"
        >
          <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 z-10"
               style={{ borderColor: "#0A1728", backgroundColor: "#B5D500" }}>
            <div>
              <h3 className="font-display font-bold text-xl leading-tight" style={{ color: "#0A1728" }}>
                Nuevo usuario
              </h3>
              <p className="text-xs mt-0.5" style={{ color: "#1E2A3D" }}>
                Los campos con * son obligatorios
              </p>
            </div>
            <button type="button" onClick={onClose}
                    className="p-2 rounded-md transition"
                    style={{ color: "#0A1728", backgroundColor: "rgba(10,23,40,0.08)" }}>
              <X className="w-5 h-5" strokeWidth={2.5}/>
            </button>
          </div>

          <div className="p-6 space-y-4">
            {/* Datos básicos */}
            <FieldLite
              label="Nombre completo *"
              value={form.name}
              onChange={(v) => setForm({...form, name: v})}
              placeholder="Ej. María López"
              testid="new-user-name"
              invalid={form.name && !nameValid}
              hint={form.name && !nameValid ? "Mínimo 3 caracteres" : null}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FieldLite
                label="Teléfono *"
                value={form.phone}
                onChange={(v) => setForm({...form, phone: v})}
                placeholder="+51 987 654 321"
                testid="new-user-phone"
                invalid={form.phone && !phoneValid}
                hint={form.phone && !phoneValid
                  ? "Formato inválido. Usa +51... con 9-15 dígitos"
                  : phoneValid ? "✓ Válido" : null}
                hintOk={phoneValid}
              />
              <FieldLite
                label="Correo (opcional)"
                value={form.email}
                onChange={(v) => setForm({...form, email: v})}
                placeholder="usuario@dominio.pe"
                testid="new-user-email"
                type="email"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5"
                     style={{ color: "#374151" }}>Rol *</label>
              <select
                value={form.role}
                onChange={(e) => setForm({...form, role: e.target.value})}
                className="w-full rounded-md px-3 py-2.5 text-sm font-medium"
                style={{
                  backgroundColor: "#FFFFFF",
                  color: "#111827",
                  border: "1px solid #D1D5DB",
                  outline: "none",
                }}
                data-testid="new-user-role"
              >
                <option value="passenger">Pasajero</option>
                <option value="driver">Conductor (unidad propia empresa)</option>
                <option value="socio">Socio (aporta su propio vehículo)</option>
                <option value="dispatcher">Despachador</option>
                <option value="admin_ops">Admin Ops</option>
                <option value="accountant">Contador</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>

            {/* Datos del vehículo — solo si Socio */}
            {isSocio && (
              <div
                className="rounded-lg p-4 mt-2"
                style={{ backgroundColor: "rgba(10,23,40,0.08)", border: "1.5px solid #0A1728" }}
              >
                <div className="text-xs font-bold uppercase tracking-wider flex items-center gap-2 mb-3"
                     style={{ color: "#0A1728" }}>
                  <Car className="w-3.5 h-3.5" /> Datos del vehículo del Socio
                </div>
                {/* Grid 2 columnas — el modal ya no será tan largo */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FieldLite
                    label="Placa *"
                    value={form.plate}
                    onChange={(v) => setForm({...form, plate: v.toUpperCase()})}
                    placeholder="Z6D-574"
                    testid="new-user-plate"
                    invalid={form.plate && !plateValid}
                    hint={form.plate && !plateValid
                      ? "Formato: 3 caracteres, guión, 3-4 caracteres (ej. Z6D-574)"
                      : plateValid && form.plate ? "✓ Válida" : null}
                    hintOk={plateValid && form.plate}
                  />
                  <FieldLite
                    label="Marca"
                    value={form.brand}
                    onChange={(v) => setForm({...form, brand: v})}
                    placeholder="Toyota"
                    testid="new-user-brand"
                  />
                  <FieldLite
                    label="Modelo *"
                    value={form.model}
                    onChange={(v) => setForm({...form, model: v})}
                    placeholder="Yaris"
                    testid="new-user-model"
                  />
                  <FieldLite
                    label="Color"
                    value={form.color}
                    onChange={(v) => setForm({...form, color: v})}
                    placeholder="Rojo"
                    testid="new-user-color"
                  />
                  <FieldLite
                    label="Pasajeros"
                    value={form.passenger_capacity}
                    onChange={(v) => setForm({...form, passenger_capacity: v})}
                    placeholder="4"
                    testid="new-user-capacity"
                    type="number"
                  />
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5"
                           style={{ color: "#374151" }}>Tipo de vehículo *</label>
                    <select
                      value={form.vehicle_type}
                      onChange={(e) => setForm({...form, vehicle_type: e.target.value})}
                      className="w-full rounded-md px-3 py-2.5 text-sm font-medium"
                      style={{
                        backgroundColor: "#FFFFFF",
                        color: "#111827",
                        border: "1px solid #D1D5DB",
                        outline: "none",
                      }}
                      data-testid="new-user-vehicle-type"
                    >
                      <option value="sedan">Sedán</option>
                      <option value="hatchback">Hatchback</option>
                      <option value="suv">SUV</option>
                      <option value="station_wagon">Station Wagon</option>
                      <option value="pickup">Pick-up</option>
                      <option value="van">Van / Minivan</option>
                    </select>
                  </div>
                </div>
                <div className="text-[11px] mt-3 flex items-center gap-1.5" style={{ color: "#0A1728", fontWeight: 600 }}>
                  <span>ℹ</span> Se creará automáticamente una unidad en la Flota asociada al Socio.
                </div>
              </div>
            )}

            {err && (
              <div
                className="rounded-md px-3 py-2 text-sm font-bold"
                style={{
                  backgroundColor: "#0A1728",
                  color: "#FF6B6B",
                  border: "1px solid #FF6B6B",
                }}
                data-testid="new-user-error"
              >
                {err}
              </div>
            )}
          </div>

          <div className="px-6 py-3 border-t flex justify-end gap-2 rounded-b-xl sticky bottom-0"
               style={{ borderColor: "#0A1728", backgroundColor: "#B5D500" }}>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md font-semibold text-sm transition"
              style={{
                color: "#0A1728",
                border: "1.5px solid #0A1728",
                backgroundColor: "transparent",
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-5 py-2 rounded-md font-bold text-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                backgroundColor: canSubmit ? "#0A1728" : "#4A5568",
                color: canSubmit ? "#C6E90C" : "#FFFFFF",
              }}
              data-testid="new-user-submit"
            >
              {saving ? "Creando..." : "Crear usuario"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

// Input reutilizable con validación visual + tema claro forzado
const FieldLite = ({ label, value, onChange, placeholder, testid, type = "text", invalid, hint, hintOk }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5"
           style={{ color: "#374151" }}>{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-md px-3 py-2.5 text-sm font-medium transition"
      style={{
        backgroundColor: "#FFFFFF",
        color: "#111827",
        border: invalid ? "1.5px solid #DC2626" : "1px solid #D1D5DB",
        outline: "none",
      }}
      onFocus={(e) => { e.target.style.borderColor = invalid ? "#DC2626" : "#3949AB"; }}
      onBlur={(e) => { e.target.style.borderColor = invalid ? "#DC2626" : "#D1D5DB"; }}
      data-testid={testid}
    />
    {hint && (
      <div className="text-[11px] mt-1" style={{ color: hintOk ? "#059669" : "#DC2626" }}>
        {hint}
      </div>
    )}
  </div>
);

const FieldRow = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">{label}</label>
    {children}
  </div>
);

const Field = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">{label}</label>
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="office-input" data-testid={testid} />
  </div>
);


// ═════════════════════════════════════════════════════════════════════
// Modal: Importar CSV masivo de Pasajeros (pre-registro para promo)
// ═════════════════════════════════════════════════════════════════════
const ImportPassengersModal = ({ onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [countryCode, setCountryCode] = useState("51");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!file) { setErr("Selecciona un archivo .csv o .txt"); return; }
    setSaving(true);
    try {
      const res = await dataApi.importPassengers(file, countryCode || "51");
      setResult(res);
      onImported && onImported();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  const s = result?.summary || {};

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose}></div>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <form onSubmit={submit}
              className="rounded-lg shadow-2xl w-full max-w-2xl fade-in max-h-[92vh] overflow-y-auto"
              style={{ backgroundColor: "#C6E90C", color: "#0A1728" }}
              data-testid="import-passengers-modal">
          <div className="px-5 py-3 border-b flex items-center justify-between sticky top-0 z-10"
               style={{ backgroundColor: "#B5D500", borderColor: "#0A1728" }}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded flex items-center justify-center"
                   style={{ backgroundColor: "#0A1728" }}>
                <FileSpreadsheet className="w-4 h-4" strokeWidth={2} style={{ color: "#C6E90C" }}/>
              </div>
              <div>
                <h3 className="font-display font-bold text-lg leading-tight" style={{ color: "#0A1728" }}>Importar pasajeros por CSV</h3>
                <p className="text-[11px] font-semibold" style={{ color: "#1E2A3D" }}>Pre-registra teléfonos para el descuento de bienvenida 20%</p>
              </div>
            </div>
            <button type="button" onClick={onClose}
                    className="p-1.5 rounded transition"
                    style={{ color: "#0A1728", backgroundColor: "rgba(10,23,40,0.08)" }}>
              <X className="w-4 h-4" strokeWidth={2.5}/>
            </button>
          </div>

          {!result ? (
            <>
              <div className="p-5 space-y-4">
                {/* Instructions */}
                <div className="rounded-md border border-[#3949AB]/20 bg-[#EEF0FC] p-3 text-[12px] text-[#1E2A78] space-y-1.5">
                  <div className="font-semibold text-[#3949AB] uppercase tracking-wider text-[10px]">Formato del archivo</div>
                  <div>• <b>Columna 1 (obligatoria):</b> teléfono. Ej: <code className="bg-white/70 px-1 rounded">0999123456</code> o <code className="bg-white/70 px-1 rounded">+593999123456</code></div>
                  <div>• <b>Columna 2 (opcional):</b> nombre. Ej: <code className="bg-white/70 px-1 rounded">María López</code></div>
                  <div>• Separadores válidos: coma, punto y coma, tabulación.</div>
                  <div>• La primera fila puede ser encabezado (se detecta automáticamente).</div>
                  <div>• Los números que ya existan en el sistema serán <b>omitidos</b> (no se duplican).</div>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">Archivo CSV / TXT *</label>
                  <label className="office-input flex items-center gap-3 cursor-pointer hover:border-[#3949AB]/50" data-testid="import-file-picker">
                    <Upload className="w-4 h-4 text-neutral-500" strokeWidth={2}/>
                    <span className="text-sm text-neutral-700 truncate">
                      {file ? file.name : "Haz clic para elegir un archivo (.csv o .txt)"}
                    </span>
                    <input
                      type="file"
                      accept=".csv,.txt,text/csv,text/plain"
                      className="hidden"
                      data-testid="import-file-input"
                      onChange={(e) => { setFile(e.target.files?.[0] || null); setErr(""); }}
                    />
                    {file && <span className="ml-auto text-[11px] font-mono text-neutral-500">{(file.size / 1024).toFixed(1)} KB</span>}
                  </label>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
                    Código de país por defecto
                  </label>
                  <input
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="51"
                    className="office-input max-w-[140px]"
                    data-testid="import-country-code"
                  />
                  <p className="text-[11px] text-neutral-500 mt-1">
                    Se aplicará solo a números sin código internacional. Perú = <b>51</b>.
                  </p>
                </div>

                {err && (
                  <div className="chip chip-danger flex items-center gap-2" data-testid="import-error">
                    <AlertTriangle className="w-3.5 h-3.5"/> {err}
                  </div>
                )}
              </div>
              <div className="px-5 py-3 border-t border-neutral-200 bg-neutral-50 flex justify-end gap-2 rounded-b-lg sticky bottom-0">
                <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
                <button type="submit" disabled={saving || !file} className="btn-primary" data-testid="import-submit">
                  {saving ? "Procesando..." : "Importar pasajeros"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="p-5 space-y-4">
                {/* Big summary numbers */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <SummaryTile label="Filas leídas" value={s.total_lines ?? 0} tone="neutral" testid="summary-total" />
                  <SummaryTile label="Creados" value={s.created ?? 0} tone="success" testid="summary-created" />
                  <SummaryTile label="Duplicados" value={s.skipped ?? 0} tone="warning" testid="summary-skipped" />
                  <SummaryTile label="Inválidos" value={s.invalid ?? 0} tone="danger" testid="summary-invalid" />
                </div>

                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[13px] text-emerald-900 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.5}/>
                  <div>
                    <b>{s.created ?? 0}</b> pasajero(s) pre-registrado(s) exitosamente. Recibirán el descuento de bienvenida 20% en su primer viaje.
                    {(s.skipped ?? 0) > 0 && <> Se omitieron <b>{s.skipped}</b> por estar ya registrados.</>}
                    {(s.invalid ?? 0) > 0 && <> Hubo <b>{s.invalid}</b> línea(s) inválida(s) que no pudieron normalizarse.</>}
                  </div>
                </div>

                {/* Detail lists */}
                {Array.isArray(result.created) && result.created.length > 0 && (
                  <DetailList
                    title={`Creados (${result.created.length})`}
                    tone="success"
                    items={result.created.map((c) => `${c.phone}${c.name && c.name !== "Pasajero" ? " — " + c.name : ""}`)}
                    testid="import-list-created"
                  />
                )}
                {Array.isArray(result.skipped) && result.skipped.length > 0 && (
                  <DetailList
                    title={`Duplicados omitidos (${result.skipped.length})`}
                    tone="warning"
                    items={result.skipped.map((c) => `${c.phone} — ${c.reason || "ya existe"}`)}
                    testid="import-list-skipped"
                  />
                )}
                {Array.isArray(result.invalid) && result.invalid.length > 0 && (
                  <DetailList
                    title={`Inválidos (${result.invalid.length})`}
                    tone="danger"
                    items={result.invalid.map((c) => `Línea ${c.line}: "${c.value}" — ${c.reason}`)}
                    testid="import-list-invalid"
                  />
                )}
              </div>
              <div className="px-5 py-3 border-t border-neutral-200 bg-neutral-50 flex justify-between items-center gap-2 rounded-b-lg sticky bottom-0">
                <button
                  type="button"
                  onClick={() => { setResult(null); setFile(null); }}
                  className="btn-secondary"
                  data-testid="import-again"
                >
                  Importar otro archivo
                </button>
                <button type="button" onClick={onClose} className="btn-primary" data-testid="import-close">
                  Cerrar
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </>
  );
};

const SummaryTile = ({ label, value, tone, testid }) => {
  const tones = {
    neutral: "bg-neutral-50 border-neutral-200 text-neutral-900",
    success: "bg-emerald-50 border-emerald-200 text-emerald-800",
    warning: "bg-amber-50 border-amber-200 text-amber-800",
    danger:  "bg-rose-50 border-rose-200 text-rose-800",
  };
  return (
    <div className={`rounded-md border p-3 ${tones[tone] || tones.neutral}`} data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-display font-bold leading-none mt-1">{value}</div>
    </div>
  );
};

const DetailList = ({ title, items, tone, testid }) => {
  const tones = {
    success: "border-emerald-200",
    warning: "border-amber-200",
    danger:  "border-rose-200",
  };
  return (
    <details className={`rounded-md border ${tones[tone] || "border-neutral-200"}`} data-testid={testid}>
      <summary className="cursor-pointer px-3 py-2 text-xs font-semibold uppercase tracking-wider text-neutral-700 hover:bg-neutral-50">
        {title}
      </summary>
      <div className="max-h-48 overflow-y-auto px-3 py-2 text-[12px] font-mono text-neutral-700 space-y-0.5 border-t border-neutral-200 bg-neutral-50/50">
        {items.slice(0, 200).map((it, i) => (
          <div key={i}>{it}</div>
        ))}
        {items.length > 200 && <div className="text-neutral-500 italic">… y {items.length - 200} más</div>}
      </div>
    </details>
  );
};
